import axios from 'axios'
import type { AxiosResponse } from 'axios'
import { useUserStore } from '@/stores/UserStore'
import router from '@/router'
import type {
    InsightsChart,
    InsightsCompetitors,
    InsightsFilterOptions,
    InsightsSummary,
    InsightsTopDevices,
} from '@/lib/insights'

const api = axios.create()

/**
 * Axios interceptors
 */
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const user = useUserStore()

        if (error.response.status === 401) {
            user.logout()

            await router.push({ name: 'login' })
        }

        return Promise.reject(error)
    },
)

export default api

// --- Auth ---

export const authApi = {
    csrfCookie: (): Promise<AxiosResponse> => api.get('/sanctum/csrf-cookie'),
    login: (email: string, password: string): Promise<AxiosResponse> => api.post('/login', { email, password }),
    logout: (): Promise<AxiosResponse> => api.delete('/logout'),
    user: (): Promise<AxiosResponse> => api.get('/api/user'),
}

// --- Catalog ---

/** Narrowing for the active listings; an omitted key means every value of it. */
export type OnlineItemFilters = {
    country?: string | null
    currency?: string | null
}

/**
 * The listing is paged server-side, so narrowing has to travel with the request:
 * filtering the page already loaded would hide rows the next page then brings back.
 */
const onlineItemsQuery = (page: number, filters: OnlineItemFilters): string => {
    const params = new URLSearchParams({ page: String(page) })

    if (filters.country) {
        params.set('country', filters.country)
    }

    if (filters.currency) {
        params.set('currency', filters.currency)
    }

    return params.toString()
}

export const catalogApi = {
    list: (queryString: string): Promise<AxiosResponse> => api.get(`/api/catalog?${queryString}`),
    get: (id: number): Promise<AxiosResponse> => api.get(`/api/catalog/${id}`),
    competitorPrices: (id: number): Promise<AxiosResponse> => api.get(`/api/catalog/${id}/competitor-prices`),
    resellItems: <T>(id: number, page: number, filters: OnlineItemFilters = {}): Promise<AxiosResponse<T>> =>
        api.get<T>(`/api/catalog/${id}/resell-items?${onlineItemsQuery(page, filters)}`),
    buybackItems: <T>(id: number, page: number, filters: OnlineItemFilters = {}): Promise<AxiosResponse<T>> =>
        api.get<T>(`/api/catalog/${id}/buyback-items?${onlineItemsQuery(page, filters)}`),
    toggleDynamicPrice: (id: number): Promise<AxiosResponse> => api.put(`/api/catalog/${id}/toggle-dynamic-price`),
    syncBasePrice: (id: number): Promise<AxiosResponse> => api.patch(`/api/catalog/${id}/sync-base-price`),
    upload: (formData: FormData): Promise<AxiosResponse> => api.post('/api/catalog/upload', formData),
    uploadInfo: (): Promise<AxiosResponse> => api.get('/api/catalog/upload/info'),
    suggestedPrice: (id: number): Promise<AxiosResponse> => api.get(`/api/catalog/${id}/suggested-price`),
    competitorGrades: (id: number): Promise<AxiosResponse> => api.get(`/api/catalog/${id}/competitor-grades`),
    resellMarketPrices: (id: number): Promise<AxiosResponse> => api.get(`/api/catalog/${id}/market-prices/resell`),
    buybackMarketPrices: (id: number): Promise<AxiosResponse> => api.get(`/api/catalog/${id}/market-prices/buyback`),
    resellChart: (id: number): Promise<AxiosResponse> => api.get(`/api/catalog/${id}/chart/resell`),
    buybackChart: (id: number): Promise<AxiosResponse> => api.get(`/api/catalog/${id}/chart/buyback`),
}

// --- Prices ---

export const pricesApi = {
    list: (id: number, queryString: string): Promise<AxiosResponse> => api.get(`/api/prices/${id}?${queryString}`),
    exportPrices: (id: number, filters: Record<string, unknown>): Promise<AxiosResponse> =>
        api.post(`/api/prices/${id}/export`, filters),
    exportItems: (id: number, filters: Record<string, unknown>): Promise<AxiosResponse> =>
        api.post(`/api/prices/${id}/items/export`, filters),
}

// --- Strategies ---

export const strategiesApi = {
    update: (categoryId: number, data: Record<string, unknown>): Promise<AxiosResponse> =>
        api.put(`/api/strategies/${categoryId}`, data),
    toggle: (categoryId: number, enabled: boolean): Promise<AxiosResponse> =>
        api.put(`/api/strategies/${categoryId}/enabled`, { enabled }),
}

export const buybackStrategiesApi = {
    update: (categoryId: number, data: Record<string, unknown>): Promise<AxiosResponse> =>
        api.put(`/api/buyback-strategies/${categoryId}`, data),
    toggle: (categoryId: number, enabled: boolean): Promise<AxiosResponse> =>
        api.put(`/api/buyback-strategies/${categoryId}/enabled`, { enabled }),
}

// --- Competitors ---

export const competitorsApi = {
    list: (id: number, queryString: string): Promise<AxiosResponse> => api.get(`/api/competitors/${id}?${queryString}`),
    item: (id: number, itemId: number): Promise<AxiosResponse> => api.get(`/api/competitors/${id}/items/${itemId}`),
    historical: (id: number, itemId: number): Promise<AxiosResponse> =>
        api.get(`/api/competitors/${id}/items/${itemId}/historical`),
    onlineItems: (id: number, itemId: number): Promise<AxiosResponse> =>
        api.get(`/api/competitors/${id}/items/${itemId}/online-items`),
}

// --- Insights ---

export const insightsApi = {
    filters: (query: string): Promise<AxiosResponse<InsightsFilterOptions>> =>
        api.get<InsightsFilterOptions>(`/api/insights/filters?${query}`),
    summary: (query: string): Promise<AxiosResponse<InsightsSummary>> =>
        api.get<InsightsSummary>(`/api/insights/summary?${query}`),
    chart: (query: string): Promise<AxiosResponse<InsightsChart>> => api.get<InsightsChart>(`/api/insights/chart?${query}`),
    topDevices: (query: string): Promise<AxiosResponse<InsightsTopDevices>> =>
        api.get<InsightsTopDevices>(`/api/insights/top-devices?${query}`),
    competitors: (query: string): Promise<AxiosResponse<InsightsCompetitors>> =>
        api.get<InsightsCompetitors>(`/api/insights/competitors?${query}`),
}

// --- Logs ---

export const logsApi = {
    list: (): Promise<AxiosResponse> => api.get('/api/logs'),
    get: (id: number): Promise<AxiosResponse> => api.get(`/api/logs/${id}`),
}

// --- User ---

export const userApi = {
    notifications: (): Promise<AxiosResponse> => api.get('/api/user/notifications'),
}
